import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { API } from 'constants';
import client from '../../../services/library-api';

import { useViewType } from '../../../hooks/useViewType';
import { useFormState } from '../../../hooks/useFormState';
import { useSwrRequestKeysToMutateFromContext } from '../../../hooks/useSwrRequestKeysToMutateFromContext';

import {
    mutateListDataAfterCreatingItem,
    mutateListDataAfterUpdatingItem,
} from '../../../helpers/swrMutateUtils';
import { mergeTwoArraysRemovingDuplicates } from '../../../helpers/mergeTwoArraysRemovingDuplicates';

import { VIEW_TYPES } from '../../../constants/viewTypes';
import { collectionColorOptions } from '../../../constants/collections';
import { defaultErrorMessage, emptyFieldErrorMessage } from '../../../constants/errorMessages';

import Modal from '../../../design-system/Modal/Modal';
import FileInput from '../../../design-system/FileInput/FileInput';
import ErrorAlert from '../../../design-system/ErrorAlert/ErrorAlert';
import ColorPicker from '../../../design-system/ColorPicker/ColorPicker';
import ModalHeader from '../../../design-system/ModalHeader/ModalHeader';
import FormFieldWrapper from '../../../design-system/FormFieldWrapper/FormFieldWrapper';
import { Button, Input } from '../../../design-system';

const PlaybookDetailsModal = ({
    mode = 'create', // "create" | "edit"
    playbookData = null,
    setUpdatedPlaybookData = () => {},
    currentKey,
    requestKeysToMutate: _requestKeysToMutate,
    onClose,
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { formData, setFormData, fieldErrorMessages, setFieldErrorMessages, handleInputChange } =
        useFormState({
            name: '',
            description: '',
            icon_color: collectionColorOptions[0],
            cover_image: null,
        });

    const [isFormDataLoaded, setIsFormDataLoaded] = useState(mode === 'create');

    const {
        requestKeysToMutate: { playbooks: requestKeysToMutateFromContext },
    } = useSwrRequestKeysToMutateFromContext();
    const requestKeysToMutate = mergeTwoArraysRemovingDuplicates(
        _requestKeysToMutate,
        requestKeysToMutateFromContext
    );

    const { viewType } = useViewType();

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    useEffect(() => {
        if (mode === 'edit' && playbookData) {
            const { name, description, icon_color, cover_image } = playbookData;
            setFormData({ name, description, icon_color, cover_image });
            setIsFormDataLoaded(true);
        }
    }, [mode, playbookData]);

    const getRequestBody = () => {
        const isCoverImageChanged =
            formData.cover_image && typeof formData.cover_image !== 'string';

        if (!isCoverImageChanged) {
            const { cover_image, ...restFormData } = formData;
            return restFormData;
        }

        if (isCoverImageChanged) {
            const requestFormData = new FormData();

            requestFormData.append('name', formData.name);
            requestFormData.append('description', formData.description);
            requestFormData.append('icon_color', formData.icon_color);
            requestFormData.append('cover_image', formData.cover_image);

            return requestFormData;
        }
    };

    const createNewPlaybook = async () => {
        const requestBody = getRequestBody();
        const { data } = await client.post(API.ROUTES.library.playbook, requestBody);

        const playbookId = data.id;

        void mutateListDataAfterCreatingItem({ requestKeysToMutate, currentKey });

        if (viewType === VIEW_TYPES.client) {
            navigate(`/playbook/${playbookId}?mode=edit`, { state: { from: location } });
        }

        if (viewType === VIEW_TYPES.admin) {
            navigate(`/configure/playbook/${playbookId}`);
        }
    };

    const editPlaybook = async () => {
        const playbookIdToEdit = playbookData.id;
        const requestBody = getRequestBody();
        const { data } = await client.patch(
            `${API.ROUTES.library.playbook}${playbookIdToEdit}/`,
            requestBody
        );
        const { name, description, icon_color, cover_image } = data;

        setUpdatedPlaybookData((prevData) => ({
            ...prevData,
            name,
            description,
            icon_color,
            cover_image,
        }));

        mutateListDataAfterUpdatingItem({
            requestKeysToMutate,
            id: playbookIdToEdit,
            updatedPart: {
                name,
                description,
                icon_color,
                cover_image,
            },
        });
    };

    const performSavingAction = {
        create: createNewPlaybook,
        edit: editPlaybook,
    };

    const handleSaveAction = async () => {
        const areEmptyFields = !formData.name || !formData.description;
        if (areEmptyFields) {
            setFieldErrorMessages({
                name: !formData.name ? emptyFieldErrorMessage : null,
                description: !formData.description ? emptyFieldErrorMessage : null,
            });
            return;
        }

        try {
            setIsLoading(true);

            await performSavingAction[mode]();

            setIsLoading(false);
            onClose();
        } catch (e) {
            if (e.response?.data?.name) {
                setFieldErrorMessages((prevMessages) => ({
                    ...prevMessages,
                    name: e.response.data.name?.find(Boolean),
                }));
            } else {
                setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
            }
            setIsLoading(false);
        }
    };

    const handleColorSelect = (color) => {
        setFormData((prevData) => ({ ...prevData, icon_color: color }));
    };

    const modalTitle = {
        create: 'Create new playbook',
        edit: 'Edit playbook',
    };

    const confirmButtonText = {
        create: 'Create Playbook',
        edit: 'Save changes',
    };

    if (!isFormDataLoaded) {
        return <></>;
    }

    return (
        <Modal onClose={onClose} size="medium" resetPadding>
            <ModalHeader title={modalTitle[mode]} outlined onClose={onClose} />

            <div className="py-5 px-5 sm:px-8 flex flex-col gap-4">
                <div className="flex flex-col gap-4">
                    <Input
                        size="sm"
                        name="playbook_name"
                        value={formData.name}
                        label="Give your playbook a name"
                        isRequired
                        state={fieldErrorMessages.name ? 'error' : 'default'}
                        errorMessage={fieldErrorMessages.name}
                        placeholder="i.e. High Conversion Sales Calls Playbook"
                        onChange={(e) => handleInputChange('name', e.target.value)}
                    />
                    <Input
                        size="sm"
                        name="playbook_description"
                        value={formData.description}
                        label="Explain what your playbook covers"
                        isRequired
                        state={fieldErrorMessages.description ? 'error' : 'default'}
                        errorMessage={fieldErrorMessages.description}
                        placeholder="How to ..."
                        onChange={(e) => handleInputChange('description', e.target.value)}
                    />
                    <ColorPicker
                        options={collectionColorOptions}
                        selected={formData.icon_color}
                        label="Select a header color"
                        handleSelect={handleColorSelect}
                    />
                    <FormFieldWrapper label="Playbook Cover Image">
                        <FileInput
                            handleFile={(file) => handleInputChange('cover_image', file)}
                            file={formData.cover_image}
                            formatsArray={['.jpg', '.jpeg', '.png']}
                            maxSizeInBytes={5242880}
                            placeholder="Drag & drop a file to upload or click to browse"
                            shouldShowPreview
                        />
                    </FormFieldWrapper>
                </div>

                <Button
                    size="md"
                    type="primary"
                    text={confirmButtonText[mode]}
                    state={isLoading ? 'loading' : 'default'}
                    onClick={handleSaveAction}
                />
            </div>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </Modal>
    );
};

export default PlaybookDetailsModal;
